import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { InternationalizationService } from '@core/internationalization/internationalization.service';
import { ResetAppStateService } from '@core/services/reset-app-state.service';
import { AuthService } from '@features/auth/auth.service';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { catchError, throwError } from 'rxjs';

const getSlug = () => {
    const url = window.location.href;
    const urlArr = url.split('/');
    return urlArr[urlArr.length - 1];
};

/** twinny-api's sign-in, sign-up, verification… (`/auth/…`), which answer 401 without a session. */
const isAuthCall = (url: string) => {
    try {
        return new URL(url, window.location.origin).pathname.includes('/auth/');
    } catch {
        return false;
    }
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const toastr = inject(ToastrService);
    const cookie = inject(CookieService);
    const authS = inject(AuthService);
    const i18nS = inject(InternationalizationService);

    const token = authS.token() || cookie.get('auth.token');
    // Product language, not the browser's: server-side texts generated in the
    // scope of this request (e.g. push notifications) should follow the UI.
    const headers: Record<string, string> = { 'Accept-Language': i18nS.getLanguage() };
    if (token) headers['authorization'] = `Bearer ${token}`;

    const request = req.clone({ withCredentials: true, setHeaders: headers });

    return next(request).pipe(
        catchError((err: HttpErrorResponse) => {
            const network = 'Please check your internet connection';
            const slug = getSlug();

            switch (err.status) {
                case 0:
                    toastr.error(network);
                    break;
                case 401:
                    // An /auth/* call's 401 is its answer (wrong password, "App X not active for this
                    // account", "Unknown app origin"…), not an expired session: the screen tells it
                    if (isAuthCall(request.url)) break;
                    if (slug !== 'login') {
                        toastr.error('Please log in again', 'Session expired');
                    }
                    authS.logout();
                    break;
            }

            return throwError(() => err);
        })
    );
};
